import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'link' | 'quiet' | 'danger';
export type ButtonSize = 'lg' | 'md' | 'sm';

const base = 'inline-flex items-center justify-center gap-2 text-center no-underline transition-colors disabled:opacity-100';
const variants: Record<ButtonVariant, string> = {
  primary: 'border-0 rounded-[10px] bg-brand text-white font-semibold shadow-btn hover:bg-brand-hover hover:text-white disabled:bg-line disabled:text-ink-2 disabled:shadow-none',
  secondary: 'border border-line-btn rounded-[10px] bg-white text-ink font-semibold hover:bg-panel hover:text-ink',
  outline: 'border border-brand rounded-[10px] bg-white text-brand font-semibold hover:bg-brand-soft',
  link: 'border-0 rounded-lg bg-transparent text-brand font-semibold hover:underline',
  quiet: 'border-0 rounded-lg bg-transparent text-muted font-semibold hover:text-ink',
  danger: 'border-0 rounded-[10px] bg-bad text-white font-semibold shadow-btn',
};
const sizes: Record<ButtonSize, string> = {
  lg: 'min-h-11 px-[22px] text-[15px]',
  md: 'min-h-11 px-4 text-[15px]',
  sm: 'min-h-9 px-3 text-sm rounded-lg',
};
const linkSizes: Record<ButtonSize, string> = { lg: 'min-h-11 px-2 text-[15px]', md: 'min-h-11 px-2 text-sm', sm: 'min-h-8 px-0 text-[13px]' };

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', extra?: string) {
  const sz = variant === 'link' || variant === 'quiet' ? linkSizes[size] : sizes[size];
  return cx(base, variants[variant], sz, extra);
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize };
export const Button = forwardRef<HTMLButtonElement, Props>(function Button({ variant, size, className, type, ...rest }, ref) {
  return <button ref={ref} type={type ?? 'button'} className={buttonClass(variant, size, className)} {...rest} />;
});

export function ButtonLink({ variant, size, className, ...rest }: LinkProps & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}

export function ButtonA({ variant, size, className, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <a className={buttonClass(variant, size, className)} {...rest} />;
}
